"use client";

import { useState, useRef, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  ChevronRight,
  X,
  Users,
  BookOpen,
  Award,
} from "lucide-react";
import Footer from "../../Header/Footer";
import Navbar from "../../Header/Navbar";
import axios from "axios";

const AnimatedSection = ({ children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const CourseCard = ({ course, onViewDetails }) => {
  return (
    <motion.div
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
      whileHover={{ y: -5 }}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          //   src={course.image || "/placeholder.svg"}
          src={`${process.env.REACT_APP_API_URL}/${course.image}`}
          alt={course.name}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
        />
        {course.featured && (
          <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
            Featured
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name}</h3>
        <p className="text-gray-600 mb-4 line-clamp-2">{course.description}</p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-2 text-blue-600" />
            Registration: {course.registration_date || course.registrationDate}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-2 text-blue-600" />
            Duration: {course.duration}
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <button
            onClick={() => onViewDetails(course)}
            className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800"
          >
            View Details <ChevronRight className="ml-1 w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const CourseDetailsModal = ({ course, isOpen, onClose }) => {
  // Close when clicking outside
  const modalRef = useRef(null);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            ref={modalRef}
            className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="relative">
              <div className="h-64 overflow-hidden">
                <img
                  src={`${process.env.REACT_APP_API_URL}/${course.image}`}
                  alt={course.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                aria-label="Close details"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
              {course.featured && (
                <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Featured
                </div>
              )}
            </div>

            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {course.name}
              </h2>

              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                  <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                  Registration:{" "}
                  {course.registration_date || course.registrationDate}
                </div>
                <div className="flex items-center text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                  Duration: {course.duration}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Course Description
                </h3>
                <p className="text-gray-700 mb-4">{course.description}</p>
                <p className="text-gray-700">
                  This comprehensive course is designed for both beginners and
                  those looking to enhance their skills. You'll learn through a
                  combination of lectures, hands-on exercises, and real-world
                  projects.
                </p>
              </div>

              {/* Course Highlights Section */}
              <div className="mb-8 bg-blue-50 rounded-xl p-6 border border-blue-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Course Highlights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100 flex items-start">
                    <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                      <Users className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        Expert Instructors
                      </div>
                      <div className="text-sm text-gray-600">
                        Learn from industry professionals
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100 flex items-start">
                    <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                      <BookOpen className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        Comprehensive Materials
                      </div>
                      <div className="text-sm text-gray-600">
                        Access to all course resources
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100 flex items-start">
                    <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                      <Award className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        Certification
                      </div>
                      <div className="text-sm text-gray-600">
                        Receive a certificate upon completion
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Content Section - Redesigned */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Course Content
                  </h3>
                  <div className="text-sm text-gray-500">
                    {course.modules ? course.modules.length : 0} modules •{" "}
                    {course.modules
                      ? course.modules.reduce(
                          (total, module) => total + module.topics.length,
                          0
                        )
                      : 0}{" "}
                    lessons
                  </div>
                </div>

                {course.modules ? (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[22px] top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full"></div>

                    <div className="space-y-6">
                      {course.modules.map((module, idx) => (
                        <div key={idx} className="relative">
                          <div className="flex">
                            {/* Module number circle */}
                            <div className="z-10 flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 shadow-md flex items-center justify-center text-white font-bold text-lg mr-4">
                              {idx + 1}
                            </div>

                            {/* Module content */}
                            <div className="flex-grow">
                              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                                  <h4 className="text-lg font-bold text-white">
                                    {module.title}
                                  </h4>
                                </div>
                                <div className="px-6 py-4">
                                  <ul className="space-y-2">
                                    {module.topics.map((topic, topicIdx) => (
                                      <li
                                        key={topicIdx}
                                        className="flex items-start"
                                      >
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 mr-3"></span>
                                        <span className="text-gray-700">
                                          {topic}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    Course module information not available
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ShortCourses = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/short-courses`
        );
        setCourses(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching short courses:", err);
        setError("Failed to load courses. Please try again later.");
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleViewDetails = (course) => {
    setSelectedCourse(course);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-r from-blue-900 to-indigo-900">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -right-40 -top-40 w-80 h-80 rounded-full bg-blue-600/20 blur-3xl"></div>
            <div className="absolute -left-20 -bottom-40 w-80 h-80 rounded-full bg-indigo-600/20 blur-3xl"></div>
          </div>
          <div className="container mx-auto px-6 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-6 inline-block"
            >
              <span className="px-4 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
                Enhance Your Skills
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
            >
              Short Courses at <span className="text-blue-300">NIIT</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto"
            >
              Accelerate your career with our industry-focused short courses
              designed to equip you with in-demand skills.
            </motion.p>
          </div>
        </section>

        {/* Courses Section */}
        <AnimatedSection>
          <section className="py-16 bg-white">
            <div className="container mx-auto px-6">
              {/* Loading and Error States */}
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative text-center max-w-2xl mx-auto">
                  {error}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
                  {courses.map((course) => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}

              {/* Course Details Modal */}
              {selectedCourse && (
                <CourseDetailsModal
                  course={selectedCourse}
                  isOpen={isModalOpen}
                  onClose={closeModal}
                />
              )}
            </div>
          </section>
        </AnimatedSection>

        {/* Why Choose Our Courses Section */}
        <AnimatedSection>
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="flex flex-col lg:flex-row items-center max-w-6xl mx-auto">
                <div className="lg:w-1/2 mb-12 lg:mb-0 lg:pr-16">
                  <div className="inline-block px-3 py-1 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
                    Why Choose Us
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
                    Learn from Industry Experts
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    Our short courses are designed and taught by professionals
                    with extensive industry experience, ensuring you receive
                    practical, relevant education.
                  </p>

                  <div className="space-y-4">
                    {[
                      {
                        title: "Industry-Relevant Curriculum",
                        description:
                          "Courses designed to meet current industry demands",
                      },
                      {
                        title: "Hands-on Learning",
                        description:
                          "Practical projects and real-world applications",
                      },
                      {
                        title: "Flexible Learning Options",
                        description: "Evening and weekend classes available",
                      },
                      {
                        title: "Professional Certification",
                        description:
                          "Receive a certificate upon successful completion",
                      },
                    ].map((item, index) => (
                      <div key={index} className="flex">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              ></path>
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {item.title}
                          </h3>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
                      alt="Students learning"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Call to Action */}
        <AnimatedSection>
          <section className="py-16 bg-gradient-to-r from-blue-800 to-indigo-900 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -right-40 -top-40 w-80 h-80 rounded-full bg-blue-600/20 blur-3xl"></div>
              <div className="absolute -left-20 -bottom-40 w-80 h-80 rounded-full bg-indigo-600/20 blur-3xl"></div>
            </div>
            <div className="container mx-auto px-6 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-white mb-6 leading-tight">
                  Ready to Advance Your Career?
                </h2>
                <p className="text-xl text-blue-200 mb-8 max-w-2xl mx-auto">
                  Enroll in our short courses today and take the first step
                  towards enhancing your professional skills.
                </p>
                <motion.a
                  href="https://wa.me/923209420163"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{
                    scale: 1.05,
                    boxShadow:
                      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="flex-shrink-0"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22c-5.523 0-10-4.477-10-10S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                  </svg>
                  <span>Register via WhatsApp</span>
                  <div className="relative ml-1">
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                    </span>
                  </div>
                </motion.a>
              </div>
            </div>
          </section>
        </AnimatedSection>
      </main>

      <Footer />
    </div>
  );
};

export default ShortCourses;
